import React from "react";
import { AppLogo } from "@/shared/components/AppLogo";
import { cn } from "@/shared/lib/cn";
import type { Agent } from "@/shared/types";
import { useAgentApps } from "./hooks/useAgentApps";

/** The agent's connected apps as overlapping logos: three, then +N. The sidebar's row draws it
    on the page (`sunk`); the composer's setup box on a white card (`card`), so each logo's ring
    cuts it out of what it sits on. */
export function AgentAppStack({ agent, on = "sunk" }: { agent: Agent; on?: "sunk" | "card" }) {
  const apps = useAgentApps(agent.id);

  if (!apps.length) return null;
  const card = on === "card";

  return (
    <div className="shrink-0 flex items-center gap-1">
      <div className={card ? "flex -space-x-[5px]" : "flex -space-x-1.5"}>
        {apps.slice(0, 3).map((app) => (
          <div key={app.slug} title={app.name}>
            <AppLogo
              logo={app.logo}
              name={app.name}
              size={card ? 18 : 16}
              iconSize={11}
              rounded={999}
              className={cn(
                "bg-[var(--surface)] overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.08)]",
                card ? "ring-[1.5px] ring-[var(--surface)]" : "ring-[1.5px] ring-[var(--surface-sunk)]",
              )}
            />
          </div>
        ))}
      </div>
      {apps.length > 3 && (
        <span
          className={cn("font-mono font-medium text-[var(--text-tertiary)] tabular-nums", card ? "text-[10.5px]" : "text-[9.5px]")}
          title={apps.slice(3).map((app) => app.name).join(", ")}
        >
          +{apps.length - 3}
        </span>
      )}
    </div>
  );
}
